import { computed, ref, watch } from 'vue'
import { useTheme } from 'vuetify'
import {
  DEFAULT_THEME_ID,
  LS_THEME,
  THEME_CATALOG,
  isEnterpriseThemeId,
  themeCssVars,
  type EnterpriseThemeId,
} from '@/themes/enterprise'

function readStoredTheme(): EnterpriseThemeId {
  try {
    const v = localStorage.getItem(LS_THEME)
    if (isEnterpriseThemeId(v)) return v
  } catch {
    /* ignore */
  }
  return DEFAULT_THEME_ID
}

const themeId = ref<EnterpriseThemeId>(readStoredTheme())
let wired = false

/**
 * Apply enterprise theme to Vuetify + document CSS variables (AG Grid / shell).
 */
export function useEnterpriseTheme() {
  const vuetifyTheme = useTheme()

  function applyCssVars(id: EnterpriseThemeId) {
    const vars = themeCssVars(id)
    const root = document.documentElement
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v)
    }
    root.dataset.acTheme = id
    root.classList.toggle('ac-theme-dark', THEME_CATALOG.find((t) => t.id === id)?.dark === true)
  }

  function setTheme(id: EnterpriseThemeId) {
    if (!isEnterpriseThemeId(id)) id = DEFAULT_THEME_ID
    themeId.value = id
    try {
      localStorage.setItem(LS_THEME, id)
    } catch {
      /* ignore */
    }
    vuetifyTheme.global.name.value = id
    applyCssVars(id)
  }

  if (!wired) {
    wired = true
    // Initial apply
    vuetifyTheme.global.name.value = themeId.value
    applyCssVars(themeId.value)
    watch(themeId, (id) => {
      vuetifyTheme.global.name.value = id
      applyCssVars(id)
    })
  }

  const meta = computed(() => THEME_CATALOG.find((t) => t.id === themeId.value) || THEME_CATALOG[0])

  return {
    themeId,
    meta,
    catalog: THEME_CATALOG,
    setTheme,
  }
}
